/**
 * Dynamic Avatar Utility for Lucy Garden
 * Generates deterministic, vibrant, distinct vector avatars based on user name/phone.
 */

export const AVATAR_PALETTES = [
  {
    name: 'Emerald',
    shirt: '#059669',
    bg: '#ECFDF5',
    border: '#10b981',
    ring: 'ring-emerald-500/30',
    badge: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300'
  },
  {
    name: 'Royal Blue',
    shirt: '#2563EB',
    bg: '#EFF6FF',
    border: '#3b82f6',
    ring: 'ring-blue-500/30',
    badge: 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300'
  },
  {
    name: 'Amber Gold',
    shirt: '#D97706',
    bg: '#FFFBEB',
    border: '#f59e0b',
    ring: 'ring-amber-500/30',
    badge: 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300'
  },
  {
    name: 'Deep Violet',
    shirt: '#7C3AED',
    bg: '#F5F3FF',
    border: '#8b5cf6',
    ring: 'ring-purple-500/30',
    badge: 'bg-purple-50 text-purple-700 dark:bg-purple-950/40 dark:text-purple-300'
  },
  {
    name: 'Vibrant Rose',
    shirt: '#E11D48',
    bg: '#FFF1F2',
    border: '#f43f5e',
    ring: 'ring-rose-500/30',
    badge: 'bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300'
  },
  {
    name: 'Teal Ocean',
    shirt: '#0D9488',
    bg: '#F0FDFA',
    border: '#14b8a6',
    ring: 'ring-teal-500/30',
    badge: 'bg-teal-50 text-teal-700 dark:bg-teal-950/40 dark:text-teal-300'
  },
  {
    name: 'Electric Indigo',
    shirt: '#4F46E5',
    bg: '#EEF2FF',
    border: '#6366f1',
    ring: 'ring-indigo-500/30',
    badge: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300'
  },
  {
    name: 'Sunset Orange',
    shirt: '#EA580C',
    bg: '#FFF7ED',
    border: '#f97316',
    ring: 'ring-orange-500/30',
    badge: 'bg-orange-50 text-orange-700 dark:bg-orange-950/40 dark:text-orange-300'
  },
  {
    name: 'Sky Cyan',
    shirt: '#0891B2',
    bg: '#ECFEFF',
    border: '#06b6d4',
    ring: 'ring-cyan-500/30',
    badge: 'bg-cyan-50 text-cyan-700 dark:bg-cyan-950/40 dark:text-cyan-300'
  },
  {
    name: 'Ruby Crimson',
    shirt: '#BE123C',
    bg: '#FFE4E6',
    border: '#e11d48',
    ring: 'ring-red-500/30',
    badge: 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300'
  },
  {
    name: 'Modern Slate',
    shirt: '#475569',
    bg: '#F1F5F9',
    border: '#64748b',
    ring: 'ring-slate-500/30',
    badge: 'bg-slate-50 text-slate-700 dark:bg-slate-950/40 dark:text-slate-300'
  },
  {
    name: 'Fresh Olive',
    shirt: '#65A30D',
    bg: '#F7FEE7',
    border: '#84cc16',
    ring: 'ring-lime-500/30',
    badge: 'bg-lime-50 text-lime-700 dark:bg-lime-950/40 dark:text-lime-300'
  }
];

/**
 * Returns a consistent palette for any name or phone seed
 */
export function getAvatarPalette(seed = '') {
  const str = String(seed || 'Lucy Garden').trim();
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  const index = Math.abs(hash) % AVATAR_PALETTES.length;
  return AVATAR_PALETTES[index];
}

/**
 * Generates an SVG Data URI with custom shirt & background circle colors
 */
export function getRetailerAvatar(seed = '', badge = null) {
  const palette = getAvatarPalette(seed);
  let badgeSvg = '';
  if (badge === 'plus' || badge === 'add') {
    badgeSvg = `
    <g id="AddBadge">
      <circle cx="385" cy="385" r="92" fill="#FFFFFF"></circle>
      <circle cx="385" cy="385" r="76" fill="#1E3A5F"></circle>
      <g transform="translate(339.4, 339.4) scale(3.8)" stroke="#FFFFFF" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round">
        <line x1="12" y1="5" x2="12" y2="19"></line>
        <line x1="5" y1="12" x2="19" y2="12"></line>
      </g>
    </g>`;
  } else if (badge === 'edit' || badge === 'pencil') {
    badgeSvg = `
    <g id="EditBadge">
      <circle cx="385" cy="385" r="92" fill="#FFFFFF"></circle>
      <circle cx="385" cy="385" r="76" fill="#2563EB"></circle>
      <g transform="translate(339.4, 339.4) scale(3.8)" stroke="#FFFFFF" stroke-width="2.6" fill="none" stroke-linecap="round" stroke-linejoin="round">
        <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"></path>
        <path d="m15 5 4 4"></path>
      </g>
    </g>`;
  }

  const svg = `<svg width="100%" height="100%" version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 508 508">
    <circle fill="${palette.bg}" cx="254" cy="254" r="254"></circle>
    <path fill="#F5C5A3" d="M254.4,322.8C254,322.8,254,322.8,254.4,322.8c-0.4,0-0.4,0-0.4,0c-60.8-29.6-54-68-54-68 c0-0.8,0-1.2,0.4-2c0.4,0.4,0.8,0.8,1.2,1.6c14,21.6,32.8,36.8,52.8,36.8c20.4,0,38.8-15.2,53.2-37.2c0.4-0.4,0.4-0.8,0.8-0.8 c0,0.4,0.4,1.2,0.4,1.6c0,0,0,0.4,0,0.8C309.2,261.6,309.6,296,254.4,322.8z"></path>
    <path fill="${palette.shirt}" d="M254,508c82,0,154.4-38.8,200.8-98.8l-26-80l-109.2-52.8c0,18.8-0.8,51.2-6.8,92.8 c-13.6-42.8-58.8-46.4-58.8-46.4s-45.2,3.6-58.8,46.4c-6-41.6-6.8-74.4-6.8-92.8L78.8,329.2l-26,80C99.6,469.2,172,508,254,508z"></path>
    <g fill="#FFFFFF">
      <path d="M308.4,254.8c0,0,6.8,38.4-54.4,68.4c0,0,45.6,3.6,58.8,46.4C313.6,370.8,338.4,277.6,308.4,254.8z"></path>
      <path d="M199.6,254.8c-30,22.8-5.2,116-4.8,114.4c13.6-42.8,59.2-46.4,59.2-46.4 C192.8,293.2,199.6,254.8,199.6,254.8z"></path>
    </g>
    <g fill="#FCD3BA">
      <path d="M316.8,236L316.8,236c-14.8,31.2-37.2,54.8-62.8,54.8c-27.6,0-51.6-27.6-66.4-62.4 c-5.6-0.4-12-4.8-15.2-12c-3.6-7.2-3.2-14.8,0-19.6c6.8,18.4,15.2,30.8,15.2,30.8c-19.6-45.6,15.2-98.8,15.2-98.8 c53.6,13.6,76.4-2.8,76.4-2.8c39.2-6.8,43.6,18.8,42.4,35.2c-0.8,9.2,0.4,18,1.6,27.2C326,211.2,318.4,232.8,316.8,236z"></path>
      <path d="M335.6,216.4c-3.2,6.4-8,10.8-13.2,12c4-6.4,9.6-16.4,14.8-28.4 C338.8,204.8,338.4,210.8,335.6,216.4z"></path>
    </g>
    <g fill="#1E293B">
      <path d="M320.4,76.8C289.2,47.6,254,46.4,254,46.4c6.8,5.2,0,10.8,0,10.8c-11.6-14.4-32-5.6-32-5.6 c14,4,18.4,11.2,18.4,11.2c-45.2-14.8-68,27.2-68,27.2l18.4-3.6C130,141.2,187.6,228,187.6,228c-19.6-45.6,15.2-98.8,15.2-98.8 c53.6,13.6,76.4-2.8,76.4-2.8c39.2-6.8,43.6,18.8,42.4,35.2c-0.8,9.2,0.4,18,1.6,27.2c3.2,24.8-6.4,47.6-6.4,47.6 S388.4,141.6,320.4,76.8z"></path>
      <path d="M322.4,228.4c-2.8,4.4-4.8,7.2-5.6,8l0,0c0,0,0,0,0-0.4c1.2-2.4,2.4-4.8,3.2-7.6 C320.8,228.4,321.6,228.4,322.4,228.4z"></path>
    </g>
    ${badgeSvg}
  </svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}
